import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Download, ShieldCheck, RefreshCw, MonitorSmartphone,
  CandlestickChart, FlaskConical, Lock, Cpu, CheckCircle2,
} from "lucide-react";
import { Logo } from "@/components/Logo";

/* Downloads hub. First and currently only project: IMMBRAIN. The download
   button reads the latest GitHub release so it always points at the newest
   installer; it degrades to the releases page if the API is unreachable. */

const REPO = "malickecase28-hash/IMM-App-updates";
const RELEASES = `https://github.com/${REPO}/releases/latest`;

const FEATURES = [
  { icon: CandlestickChart, title: "Live charting", body: "Professional charts wired to your broker in real time, with full indicators and your account in view." },
  { icon: FlaskConical, title: "Strategy discovery", body: "Run disciplined research across instruments from the app, with honest calendar checks before anything launches." },
  { icon: Lock, title: "Licensed access", body: "Company-key accounts, device-bound and time-limited. Your desk decides who runs it and for how long." },
  { icon: MonitorSmartphone, title: "Watch anywhere", body: "A clean desktop station with a responsive mobile view, plus secure remote access from your phone." },
  { icon: Cpu, title: "Engine included", body: "The full quant engine ships inside the installer. The command line is there too, for power users." },
  { icon: RefreshCw, title: "Updates itself", body: "New versions arrive with a one-click popup. Download, restart, done. No reinstalling by hand." },
];

const REQUIREMENTS = [
  "Windows 10 or 11 (64-bit)",
  "MetaTrader 5 from your broker, signed in",
  "Python 3 (the setup screen installs the connector for you)",
  "About 400 MB of free space",
];

function fmtSize(bytes) {
  if (!bytes) return null;
  return `${(bytes / 1048576).toFixed(0)} MB`;
}

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const fn = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      style={{
        position: "fixed", bottom: 28, right: 28, zIndex: 999,
        width: 44, height: 44, borderRadius: "50%",
        background: "#DC2626", color: "#fff", border: "none",
        cursor: "pointer", fontSize: 18, lineHeight: 1,
        boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
      }}
    >↑</button>
  );
}

export default function DownloadsPage() {
  const [release, setRelease] = useState(null);

  useEffect(() => {
    document.title = "Downloads - Malicke Case";
    fetch(`https://api.github.com/repos/${REPO}/releases/latest`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        const exe = (data.assets || []).find((a) => a.name.toLowerCase().endsWith(".exe"));
        setRelease({
          version: data.tag_name || data.name,
          date: data.published_at ? new Date(data.published_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null,
          url: exe ? exe.browser_download_url : RELEASES,
          size: exe ? fmtSize(exe.size) : null,
        });
      })
      .catch(() => undefined);
    return () => { document.title = "Malicke Case"; };
  }, []);

  const downloadUrl = release?.url || RELEASES;

  return (
    <div className="min-h-screen bg-ink-950 text-zinc-100 antialiased">
      {/* ambient glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-magenta-500/10 blur-[140px]" />
      </div>

      <div className="relative mx-auto max-w-6xl px-5 py-8 sm:px-8">
        <Link to="/" aria-label="Malicke Case home">
          <Logo compact className="h-8 w-auto" />
        </Link>

        {/* hero */}
        <section className="grid items-center gap-10 pt-14 lg:grid-cols-2 lg:pt-20">
          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-magenta-300">Downloads</span>
            <h1 className="mt-3 text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">IMMBRAIN</h1>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-zinc-400">
              A complete quantitative trading station. Charting, research, live monitoring, and licensed access in one signed desktop app.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-xl bg-zinc-700/50 px-5 py-3 text-sm font-semibold text-zinc-500 cursor-not-allowed select-none">
                <Download className="h-4 w-4" /> Download for Windows
              </span>
              <Link to="/immbrain"
                className="inline-flex items-center gap-2 rounded-xl border border-edge-700 px-5 py-3 text-sm font-medium text-zinc-200 transition-colors hover:border-magenta-500/60 hover:bg-graphite-900/50">
                See it live
              </Link>
            </div>
            <p className="mt-3 text-[12px] text-zinc-600 italic">Downloads are currently unavailable  -  might be back in the future.</p>

            <div className="mt-4 font-mono text-[11px] text-zinc-500">
              {release?.version ? `${release.version}` : "Latest"}
              {release?.size ? ` · ${release.size}` : ""}
              {release?.date ? ` · ${release.date}` : ""}
              {" · 64-bit installer"}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="relative">
            <div className="overflow-hidden rounded-2xl border border-edge-700 bg-graphite-900 shadow-2xl shadow-black/50">
              <img src="/downloads/immbrain-desktop.png" alt="IMMBRAIN desktop" className="w-full" loading="lazy" />
            </div>
            <div className="absolute -bottom-8 -right-2 hidden w-28 overflow-hidden rounded-2xl border border-edge-700 bg-graphite-900 shadow-2xl shadow-black/60 sm:block">
              <img src="/downloads/immbrain-mobile.png" alt="IMMBRAIN mobile" className="w-full" loading="lazy" />
            </div>
          </motion.div>
        </section>

        {/* trust strip */}
        <section className="mt-20 grid gap-px overflow-hidden rounded-2xl border border-edge-700 bg-edge-700 sm:grid-cols-3">
          {[
            { icon: ShieldCheck, title: "Signed installer", body: "A real Windows setup, code-signed end to end." },
            { icon: Lock, title: "Your data stays local", body: "Market data and results live on your machine, never ours." },
            { icon: RefreshCw, title: "Automatic updates", body: "New versions arrive in-app with one click." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="bg-ink-900 p-6">
              <Icon className="h-5 w-5 text-magenta-300" />
              <h3 className="mt-3 text-sm font-semibold">{title}</h3>
              <p className="mt-1 text-[13px] leading-relaxed text-zinc-400">{body}</p>
            </div>
          ))}
        </section>

        {/* features */}
        <section className="mt-20">
          <h2 className="text-2xl font-semibold tracking-tight">Everything in one station</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(({ icon: Icon, title, body }, i) => (
              <motion.div key={title}
                initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.06 }}
                className="rounded-2xl border border-edge-700 bg-graphite-900/60 p-6 transition-colors hover:border-magenta-500/40">
                <Icon className="h-5 w-5 text-magenta-300" />
                <h3 className="mt-4 text-[15px] font-semibold">{title}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-zinc-400">{body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* requirements + cta */}
        <section className="mt-20 grid gap-8 lg:grid-cols-2">
          <div className="rounded-2xl border border-edge-700 bg-graphite-900/60 p-7">
            <h2 className="text-lg font-semibold tracking-tight">Before you install</h2>
            <ul className="mt-5 space-y-3">
              {REQUIREMENTS.map((r) => (
                <li key={r} className="flex items-start gap-3 text-[14px] text-zinc-300">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-magenta-300" /> {r}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-[12px] leading-relaxed text-zinc-500">
              You will need a company key to create your account. If you do not have one, reach out and I will issue it.
            </p>
          </div>

          <div className="flex flex-col justify-center rounded-2xl border border-magenta-500/30 bg-gradient-to-br from-graphite-900 to-ink-900 p-7">
            <h2 className="text-xl font-semibold tracking-tight">Ready to start</h2>
            <p className="mt-2 text-[14px] leading-relaxed text-zinc-400">
              Download the installer, create your account with your key, and the setup walkthrough takes care of the rest.
            </p>
            <span className="mt-6 inline-flex w-fit items-center gap-2 rounded-xl bg-zinc-700/50 px-5 py-3 text-sm font-semibold text-zinc-500 cursor-not-allowed select-none">
              <Download className="h-4 w-4" /> Download IMMBRAIN
            </span>
            <p className="mt-3 text-[12px] text-zinc-600 italic">Downloads are currently unavailable  -  might be back in the future.</p>
          </div>
        </section>

        <footer className="mt-24 border-t border-edge-700 py-8 text-center font-mono text-[11px] text-zinc-600">
          IMMBRAIN by Malicke Case · malickecase.com
        </footer>
      </div>
      <BackToTop />
    </div>
  );
}
